import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { TASK_STATUSES, PRIORITIES, label, item } from "@/lib/constants";
import { fmtDateTime, fromNow, initials, minutesToHours } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Play, Square, Loader2, X, MessageSquare, Send } from "lucide-react";

const empty = {
  title: "", description: "", project_id: "", status: "backlog", priority: "medium",
  task_type: "Genel Görev", assignees: [], due_date: "", checklist: [],
};

export default function TaskDialog({ open, onOpenChange, taskId, defaultProject, onSaved }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState("");
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [newCheck, setNewCheck] = useState("");

  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: async () => (await api.get("/org/members")).data, enabled: open });
  const { data: projects = [] } = useQuery({ queryKey: ["projects-min"], queryFn: async () => (await api.get("/projects")).data, enabled: open });

  useEffect(() => {
    if (!open) return;
    if (taskId) {
      api.get(`/tasks/${taskId}`).then(({ data }) => {
        setDetail(data);
        setForm({ ...empty, ...data, project_id: data.project_id || "", due_date: data.due_date ? data.due_date.slice(0, 10) : "" });
      }).catch(() => {
        toast.error("Görev bulunamadı veya silinmiş");
        onOpenChange(false);
      });
    } else {
      setDetail(null);
      setForm({ ...empty, project_id: defaultProject || "" });
    }
  }, [open, taskId, defaultProject]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Başlık gerekli"); return; }
    setSaving(true);
    try {
      const payload = { ...form, project_id: form.project_id || null, due_date: form.due_date || null };
      if (taskId) await api.patch(`/tasks/${taskId}`, payload);
      else await api.post("/tasks", payload);
      toast.success(taskId ? "Görev güncellendi" : "Görev oluşturuldu");
      qc.invalidateQueries();
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleAssignee = (id) => {
    set("assignees", form.assignees.includes(id) ? form.assignees.filter((a) => a !== id) : [...form.assignees, id]);
  };

  const addCheck = () => {
    if (!newCheck.trim()) return;
    set("checklist", [...form.checklist, { id: Math.random().toString(36).slice(2), text: newCheck, done: false }]);
    setNewCheck("");
  };
  const toggleCheck = (id) => set("checklist", form.checklist.map((c) => c.id === id ? { ...c, done: !c.done } : c));
  const rmCheck = (id) => set("checklist", form.checklist.filter((c) => c.id !== id));

  const runAI = async () => {
    if (!aiText.trim()) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/parse-task", { text: aiText });
      setForm((f) => ({ ...f, title: data.title || f.title, description: data.description || f.description,
        priority: data.priority || f.priority, due_date: data.due_date || f.due_date,
        checklist: data.checklist?.map((c) => ({ id: Math.random().toString(36).slice(2), text: c.text, done: false })) || f.checklist }));
      toast.success("AI görev taslağı oluşturdu");
      setAiText("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  const sendComment = async () => {
    if (!comment.trim()) return;
    await api.post(`/tasks/${taskId}/comments`, { body: comment });
    setComment("");
    const { data } = await api.get(`/tasks/${taskId}`);
    setDetail(data);
  };

  const startTimer = async () => {
    try { await api.post("/timer/start", { task_id: taskId }); toast.success("Zamanlayıcı başladı"); qc.invalidateQueries(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const checkDone = form.checklist.filter((c) => c.done).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle>{taskId ? `Görev #${detail?.number || ""}` : "Yeni Görev"}</DialogTitle>
        </DialogHeader>

        {!taskId && (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 dark:bg-indigo-950/30 dark:border-indigo-900 p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-2"><Sparkles className="h-4 w-4" /> AI ile hızlı oluştur</div>
            <div className="flex gap-2">
              <Input value={aiText} onChange={(e) => setAiText(e.target.value)} placeholder="Örn: Cuma gününe kadar müşteri sunumunu hazırla, yüksek öncelik" data-testid="ai-task-input" />
              <Button type="button" onClick={runAI} disabled={aiLoading} data-testid="ai-task-run">
                {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label>Başlık</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" data-testid="task-title-input" />
          </div>
          <div>
            <Label>Açıklama</Label>
            <Textarea value={form.description?.replace(/<[^>]+>/g, "")} onChange={(e) => set("description", e.target.value)} className="mt-1.5" rows={3} data-testid="task-description-input" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="mt-1.5" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Öncelik</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger className="mt-1.5" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Proje</Label>
              <Select value={form.project_id || "none"} onValueChange={(v) => set("project_id", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5" data-testid="task-project-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Proje yok</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bitiş Tarihi</Label>
              <Input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} className="mt-1.5" data-testid="task-due-input" />
            </div>
          </div>

          <div>
            <Label>Atananlar</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {members.map((m) => (
                <button key={m.id} type="button" onClick={() => toggleAssignee(m.id)}
                  className={`flex items-center gap-1.5 rounded-full pl-1 pr-2.5 py-1 text-xs border transition-colors ${form.assignees.includes(m.id) ? "bg-indigo-600 text-white border-indigo-600" : "bg-muted hover:bg-accent"}`}
                  data-testid={`assignee-${m.id}`}>
                  <Avatar className="h-5 w-5"><AvatarFallback className="text-[9px]">{initials(m.name)}</AvatarFallback></Avatar>
                  {m.name}
                </button>
              ))}
            </div>
          </div>

          {/* Checklist */}
          <div>
            <div className="flex items-center justify-between">
              <Label>Kontrol Listesi {form.checklist.length > 0 && <span className="text-muted-foreground">({checkDone}/{form.checklist.length})</span>}</Label>
            </div>
            {form.checklist.length > 0 && <Progress value={form.checklist.length ? (checkDone / form.checklist.length) * 100 : 0} className="h-1.5 my-2" />}
            <div className="space-y-1.5">
              {form.checklist.map((c) => (
                <div key={c.id} className="flex items-center gap-2 group">
                  <Checkbox checked={c.done} onCheckedChange={() => toggleCheck(c.id)} data-testid={`check-${c.id}`} />
                  <span className={`text-sm flex-1 ${c.done ? "line-through text-muted-foreground" : ""}`}>{c.text}</span>
                  <button onClick={() => rmCheck(c.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input value={newCheck} onChange={(e) => setNewCheck(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCheck())} placeholder="Yeni madde..." className="h-8" data-testid="checklist-input" />
              <Button type="button" variant="outline" size="sm" onClick={addCheck} data-testid="checklist-add"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>

          {/* Detail-only: time + comments */}
          {taskId && detail && (
            <>
              <div className="flex items-center gap-3 text-sm border-t pt-3">
                <span className="text-muted-foreground">Harcanan: {minutesToHours(detail.actual_minutes)}</span>
                <Button type="button" variant="outline" size="sm" onClick={startTimer} data-testid="task-start-timer"><Play className="h-3.5 w-3.5 mr-1" /> Zamanlayıcı Başlat</Button>
              </div>
              <div className="border-t pt-3">
                <div className="flex items-center gap-2 text-sm font-medium mb-2"><MessageSquare className="h-4 w-4" /> Yorumlar ({detail.comments?.length || 0})</div>
                <div className="space-y-3 max-h-48 overflow-y-auto mb-2">
                  {detail.comments?.map((c) => (
                    <div key={c.id} className="flex gap-2">
                      <Avatar className="h-7 w-7"><AvatarFallback className="text-[9px]">{initials(c.user_name)}</AvatarFallback></Avatar>
                      <div>
                        <p className="text-xs"><span className="font-medium">{c.user_name}</span> <span className="text-muted-foreground">{fromNow(c.created_at)}</span></p>
                        <p className="text-sm">{c.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendComment()} placeholder="Yorum ekle..." data-testid="task-comment-input" />
                  <Button type="button" onClick={sendComment} size="icon" data-testid="task-comment-send"><Send className="h-4 w-4" /></Button>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button onClick={save} disabled={saving} data-testid="task-save-button">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {taskId ? "Kaydet" : "Oluştur"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
